import React, { useEffect, useMemo, useRef, useState } from 'react';
import { localePath } from 'utils/localePath';
import { createLatestRequest } from 'utils/latestRequest';

const API_PATH = '/api/revenue_calculation';
const DEBOUNCE_MS = 250;
// Range of the price slider on the tool page; the number field accepts any price.
const PRICE_SLIDER_MIN = 200;
const PRICE_SLIDER_MAX = 3000;

async function fetchCalculation(params) {
  const query = new URLSearchParams(params).toString();
  const response = await fetch(`${API_PATH}?${query}`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`calculation failed: ${response.status}`);
  return response.json();
}

/**
 * RevenueCalculator — shows how one lesson's price is split.
 *
 * Every amount comes from the server (Api::RevenueCalculationsController),
 * which uses the payslip rules. This component only holds the inputs and
 * displays the latest result; it does no money arithmetic.
 *
 * Props (read from data-* attributes by app/javascript/pages/faq-calculator.js):
 * - initialCalculation: server-computed result for the default inputs
 * - showComparison: show the other-platform comparison (tool page only)
 * - feeLabels: translations from fee_labels
 */
export default function RevenueCalculator({ initialCalculation, showComparison = false, feeLabels = {} }) {
  const [result, setResult] = useState(initialCalculation);
  const [failed, setFailed] = useState(false);
  const initialInputs = initialCalculation?.inputs || {};
  const limits = result?.limits || initialCalculation?.limits || {};

  const [price, setPrice] = useState(initialInputs.price ?? 1000);
  const [source, setSource] = useState(initialInputs.source || 'invited');
  const [commissionBySource, setCommissionBySource] = useState({
    invited: limits.invited?.default_owner_commission_pct ?? 0,
    platform: limits.platform?.default_owner_commission_pct ?? 0
  });
  const [referral, setReferral] = useState(initialInputs.referral_bonus_pct ?? 0);
  const [lessonsPerMonth, setLessonsPerMonth] = useState(initialInputs.lessons_per_month ?? 10);
  const [teacherCount, setTeacherCount] = useState(initialInputs.teacher_count ?? 5);

  const ownerCommission = commissionBySource[source];
  const maxCommission = limits[source]?.max_owner_commission_pct ?? 0;
  // The referral slider cannot pass the owner commission; the server applies the same bound.
  const referralShown = Math.min(referral, ownerCommission);

  const request = useMemo(() => createLatestRequest(fetchCalculation), []);
  const firstRender = useRef(true);

  useEffect(() => {
    // First paint uses the server-computed initial result: no request.
    if (firstRender.current) {
      firstRender.current = false;
      return undefined;
    }
    // A blank field is not a number yet: keep the last result until it is filled.
    if ([price, lessonsPerMonth, teacherCount].some(value => value === '')) return undefined;
    const timer = setTimeout(async () => {
      const outcome = await request({
        locale: document.documentElement.lang || '',
        price,
        source,
        owner_commission_pct: ownerCommission,
        referral_bonus_pct: referralShown,
        lessons_per_month: lessonsPerMonth,
        teacher_count: teacherCount,
        include_competitors: showComparison ? 'true' : 'false'
      });
      if (outcome.status === 'ok') {
        setResult(outcome.data);
        setFailed(false);
      } else if (outcome.status === 'error') {
        setFailed(true);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [price, source, ownerCommission, referralShown, lessonsPerMonth, teacherCount, showComparison, request]);

  if (!result) return null;

  const setOwnerCommission = (value) => setCommissionBySource(prev => ({ ...prev, [source]: value }));
  const wholeNumber = (value) => (value === '' ? '' : Math.max(0, parseInt(value, 10) || 0));

  const basicInputs = (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {showComparison ? (feeLabels.student_pays_per_lesson || 'What the student pays per lesson') : (feeLabels.lesson_price || 'Lesson price')}
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">NT$</span>
            <input
              type="number"
              min="0"
              step="50"
              value={price}
              onChange={(e) => setPrice(wholeNumber(e.target.value))}
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#3366FF] focus:ring-2 focus:ring-[#3366FF]/20 outline-none transition-all text-lg font-medium"
            />
          </div>
          {showComparison && (
            <input
              type="range"
              min={PRICE_SLIDER_MIN}
              max={PRICE_SLIDER_MAX}
              step="50"
              value={price === '' ? PRICE_SLIDER_MIN : Math.min(price, PRICE_SLIDER_MAX)}
              onChange={(e) => setPrice(parseInt(e.target.value, 10))}
              aria-label={feeLabels.lesson_price || 'Lesson price'}
              className="w-full mt-3 accent-[#3366FF] cursor-pointer"
            />
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{feeLabels.lessons_per_month || 'Lessons per month'}</label>
          <input
            type="number"
            min="0"
            max="200"
            value={lessonsPerMonth}
            onChange={(e) => setLessonsPerMonth(wholeNumber(e.target.value))}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#3366FF] focus:ring-2 focus:ring-[#3366FF]/20 outline-none transition-all text-lg font-medium"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{feeLabels.student_source || 'Student source'}</label>
        <div className="inline-flex rounded-full bg-gray-100 p-1">
          {['invited', 'platform'].map(key => (
            <button
              key={key}
              type="button"
              onClick={() => setSource(key)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                source === key ? 'bg-white text-[#3366FF] shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {key === 'invited' ? (feeLabels.invited_student || 'Invited student') : (feeLabels.platform_student || 'Platform student')}
              {' '}({limits[key]?.platform_fee_pct ?? 0}%)
            </button>
          ))}
        </div>
        {showComparison && <p className="text-xs text-gray-400 mt-1">{feeLabels.source_affects_tn_only}</p>}
      </div>
    </div>
  );

  const notices = (
    <>
      {failed && (
        <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">{feeLabels.request_failed || 'Could not update the numbers. The last result is still shown.'}</p>
      )}
      {result.capped && (
        <p className="text-xs text-gray-500">{feeLabels.capped_hint || 'The owner commission is limited to the maximum.'}</p>
      )}
    </>
  );

  const breakdown = (
    <div className="space-y-6">
      <PercentSlider
        label={feeLabels.owner_commission || 'Owner commission'}
        value={ownerCommission}
        max={maxCommission}
        onChange={setOwnerCommission}
        accent="#3366FF"
      />
      {ownerCommission > 0 && (
        <PercentSlider
          label={feeLabels.referral_bonus || 'Referral bonus'}
          hint={feeLabels.referral_bonus_hint}
          value={referralShown}
          max={ownerCommission}
          onChange={setReferral}
          accent="#14B8A6"
        />
      )}
      <BreakdownBar perLesson={result.per_lesson} feeLabels={feeLabels} />
      <BreakdownDetails result={result} referral={referralShown} feeLabels={feeLabels} />
      {result.per_lesson.owner_commission > 0 && (
        <OwnerEstimate
          result={result}
          teacherCount={teacherCount}
          onTeacherCountChange={(value) => setTeacherCount(wholeNumber(value))}
          feeLabels={feeLabels}
        />
      )}
    </div>
  );

  // Tool page: the comparison comes first and every platform moves with the inputs.
  if (showComparison && result.comparison) {
    return (
      <div className="space-y-6">
        {basicInputs}
        {notices}
        <CompareBars comparison={result.comparison} source={source} feeLabels={feeLabels} />
        <details className="rounded-xl border border-gray-200 p-4">
          <summary className="cursor-pointer text-sm font-semibold text-[#2952CC]">{feeLabels.tn_breakdown_toggle || 'How one TutorNetworking lesson is split'}</summary>
          <div className="mt-4">{breakdown}</div>
        </details>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {basicInputs}
      {notices}
      {breakdown}
      <a
        href={localePath('/tools/teacher-take-home')}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-[#3366FF] hover:text-[#2952CC] transition-colors"
      >
        {feeLabels.see_comparison || 'Compare with other platforms'}
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </a>
    </div>
  );
}

function PercentSlider({ label, hint, value, max, onChange, accent }) {
  const ratio = max > 0 ? value / max : 0;
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {hint && <p className="text-xs text-gray-400 mb-3">{hint}</p>}
      <div className="relative pt-6">
        <div
          className="absolute top-0 text-white rounded-md px-2 py-0.5 text-xs font-bold pointer-events-none"
          style={{ left: `calc(${ratio * 100}% + ${8 - ratio * 16}px)`, transform: 'translateX(-50%)', backgroundColor: accent }}
        >
          {value}%
        </div>
        <input
          type="range"
          min="0"
          max={max}
          step="1"
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          style={{ accentColor: accent }}
        />
      </div>
      <div className="flex justify-between text-xs text-gray-400 mt-1">
        <span>0%</span>
        <span>{max}%</span>
      </div>
    </div>
  );
}

// Bar widths are a display ratio of the server amounts to the price.
function BreakdownBar({ perLesson, feeLabels }) {
  const gross = perLesson.gross;
  const share = (amount) => (gross > 0 ? (amount / gross) * 100 : 0);
  const segments = [
    { key: 'platform', pct: share(perLesson.platform_fee), color: 'bg-gray-300', text: 'text-gray-600', label: feeLabels.platform_fee || 'Platform fee' },
    { key: 'owner', pct: share(perLesson.net_owner_commission), color: 'bg-[#6699FF]', text: 'text-white', label: feeLabels.management_fee || 'Management fee' },
    { key: 'referral', pct: share(perLesson.referral_bonus), color: 'bg-teal-400', text: 'text-white', label: feeLabels.referral_bonus || 'Referral bonus' },
    { key: 'service', pct: share(perLesson.service_fee), color: 'bg-[#FBBF24]', text: 'text-white', label: feeLabels.service_fee || 'Service fee' },
    { key: 'teacher', pct: share(perLesson.teacher_earnings), color: 'bg-[#3366FF]', text: 'text-white', label: feeLabels.teacher_earnings || 'Teacher earnings' }
  ].filter(s => s.pct > 0);

  return (
    <div className="space-y-2">
      <div className="flex rounded-xl overflow-hidden h-10" role="img" aria-label={feeLabels.breakdown_aria || 'Revenue breakdown'}>
        {segments.map(({ key, pct, color, text, label }) => (
          <div key={key} className={`${color} flex items-center justify-center text-xs font-medium ${text} transition-all duration-300`} style={{ width: `${pct}%` }} title={label}>
            {pct >= 6 && `${pct.toFixed(0)}%`}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
        {segments.map(({ key, color, label }) => (
          <span key={key} className="flex items-center gap-1.5">
            <span className={`w-3 h-3 rounded-sm ${color}`}></span>
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

function Row({ label, value, className = 'text-gray-500' }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100">
      <span className="text-sm text-gray-600">{label}</span>
      <span className={`text-sm font-medium ${className}`}>{value}</span>
    </div>
  );
}

function BreakdownDetails({ result, referral, feeLabels }) {
  const lesson = result.per_lesson;
  const text = result.formatted.per_lesson;
  const rates = result.rates;
  const referralPart = referral > 0 ? ` - ${referral}% ${feeLabels.referral || 'referral'}` : '';

  return (
    <div className="space-y-3">
      <Row label={feeLabels.student_payment || 'Student payment'} value={text.gross} className="text-gray-900" />
      <Row label={`${feeLabels.platform_fee || 'Platform fee'} (${rates.platform_fee_pct}%)`} value={`-${text.platform_fee}`} />
      <Row label={`${feeLabels.owner_commission || 'Owner commission'} (${rates.owner_commission_pct}%${referralPart})`} value={`-${text.net_owner_commission}`} className="text-[#6699FF]" />
      {lesson.referral_bonus > 0 && (
        <Row label={`${feeLabels.referral_bonus || 'Referral bonus'} (${referral}%)`} value={`-${text.referral_bonus}`} className="text-teal-600" />
      )}
      <Row label={`${feeLabels.service_fee || 'Service fee'} (${rates.service_fee_pct}%)`} value={`-${text.service_fee}`} className="text-[#D97706]" />
      <div className="flex items-center justify-between py-3 bg-[#E6F0FF] rounded-xl px-4 -mx-1">
        <span className="text-sm font-bold text-gray-900">{feeLabels.teacher_earnings || 'Teacher take-home'}</span>
        <span className="text-lg font-bold text-[#3366FF]">{text.teacher_earnings}</span>
      </div>
      <div className="flex items-center justify-between px-4 text-sm text-gray-600">
        <span>{feeLabels.monthly_take_home || 'Take-home per month'}</span>
        <span className="font-semibold text-gray-900">{result.formatted.monthly.teacher_earnings}</span>
      </div>
    </div>
  );
}

function OwnerEstimate({ result, teacherCount, onTeacherCountChange, feeLabels }) {
  const estimate = result.formatted.owner_estimate;
  // The formula names the inputs the shown total was computed from.
  const { teacher_count: computedTeachers, lessons_per_month: computedLessons } = result.inputs;
  return (
    <div className="rounded-2xl border-2 border-purple-200 bg-purple-50/50 p-5 space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-lg">👑</span>
        <h4 className="text-sm font-bold text-purple-800">{feeLabels.owner_estimate_title || 'Owner earnings estimate'}</h4>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm text-purple-700">{feeLabels.net_fee_per_lesson || 'Net management fee per lesson'}</span>
        <span className="text-sm font-bold text-purple-900">{estimate.per_lesson}</span>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-sm text-purple-700 whitespace-nowrap">{feeLabels.teachers_managed || 'Teachers managed'}</label>
        <input
          type="number"
          min="0"
          max="50"
          value={teacherCount}
          onChange={(e) => onTeacherCountChange(e.target.value)}
          className="w-16 px-2 py-1.5 rounded-lg border border-purple-200 text-center text-sm font-medium focus:border-purple-400 focus:ring-2 focus:ring-purple-200 outline-none"
        />
      </div>
      <div className="bg-purple-100 rounded-xl p-4">
        <div className="text-xs text-purple-600 mb-1">{feeLabels.estimated_monthly || 'Estimated monthly net management earnings'}</div>
        <div className="text-xl font-bold text-purple-900">{estimate.monthly}</div>
        <div className="text-xs text-purple-500 mt-1">
          = {computedTeachers} {feeLabels.teachers_unit || 'teachers'} × {computedLessons} {feeLabels.lessons_unit || 'lessons'} × {estimate.per_lesson}
        </div>
      </div>
    </div>
  );
}

function SourceLink({ href, checkedOn, feeLabels, label }) {
  return (
    <span className="text-xs text-gray-400">
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#3366FF] hover:underline">
        {label || feeLabels.official_page || 'Official page'}
      </a>
      {' · '}{feeLabels.checked_on || 'Checked'} {checkedOn}
    </span>
  );
}

// Monthly take-home per platform. Bar positions are a display ratio of the
// server amounts; the widest value (what a student pays) sets the scale.
function CompareBars({ comparison, source, feeLabels }) {
  const tn = comparison.solo.find(row => row.source === source) || comparison.solo[0];
  const percentRows = comparison.competitors.filter(c => c.model === 'percentage_range');
  const noteRows = comparison.competitors.filter(c => c.model !== 'percentage_range');
  const scale = Math.max(
    1,
    tn.student_pays_month,
    ...percentRows.flatMap(r => [r.start_month, r.floor_month, r.student_pays_month || 0])
  );
  const pct = (amount) => `${(amount / scale) * 100}%`;

  return (
    <div className="space-y-4 border-t border-gray-100 pt-6">
      <div>
        <h3 className="text-lg font-bold text-gray-900">{feeLabels.compare_title || 'Compared with other platforms'}</h3>
        <p className="text-sm text-gray-600 mt-1">{feeLabels.compare_intro}</p>
      </div>

      <div className="rounded-xl bg-[#E6F0FF] p-3 space-y-2">
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-semibold text-gray-900">TutorNetworking</span>
          <span className="font-bold text-[#2952CC] whitespace-nowrap">{tn.formatted_monthly}</span>
        </div>
        <div className="h-3 rounded-full bg-white/70 relative overflow-hidden">
          <div className="absolute inset-y-0 left-0 rounded-full bg-[#3366FF] transition-all duration-300" style={{ width: pct(tn.monthly) }} />
        </div>
        <p className="text-xs text-gray-600"><span className="font-semibold">{tn.kept_label}</span> · {source === 'invited' ? feeLabels.tn_invited_how : feeLabels.tn_platform_how}</p>
        <p className="text-xs text-gray-600">{tn.student_fee_note}</p>
      </div>

      {percentRows.map(row => (
        <div key={row.key} className="rounded-xl border border-gray-100 p-3 space-y-2">
          <div className="flex items-baseline justify-between gap-3">
            <span className="font-semibold text-gray-900">{row.name}</span>
            <span className="font-semibold text-gray-800 whitespace-nowrap">{row.formatted_start_month}～{row.formatted_floor_month}</span>
          </div>
          <div className="h-3 rounded-full bg-gray-100 relative overflow-hidden">
            <div
              className="absolute inset-y-0 rounded-full bg-[#6699FF] transition-all duration-300"
              style={{ left: pct(row.start_month), width: `calc(${pct(row.floor_month - row.start_month)} + 4px)` }}
            />
          </div>
          <p className="text-xs text-gray-600"><span className="font-semibold">{row.kept_label}</span> · {row.rate_summary} <SourceLink href={row.source_url} checkedOn={row.checked_on_label} feeLabels={feeLabels} /></p>
          {row.student_fee_note && (
            <p className="text-xs text-gray-600">
              {row.student_fee_note}{' '}
              <SourceLink href={row.student_fee_source_url} checkedOn={row.student_fee_checked_on_label} feeLabels={feeLabels} />
            </p>
          )}
        </div>
      ))}

      {noteRows.map(row => (
        <p key={row.key} className="text-sm text-gray-600">
          {row.name}: {row.rate_summary} <SourceLink href={row.source_url} checkedOn={row.checked_on_label} feeLabels={feeLabels} />
        </p>
      ))}

      <p className="text-xs text-gray-500">{feeLabels.compare_footnote}</p>
    </div>
  );
}
